import { Autocomplete, Grid, TextField, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { FC, useState } from 'react';
import { ActionsList } from 'src/components/ActionsList';
import { DeleteIconButton } from 'src/components/DeleteIconButton';
import { dataTestIds } from 'src/constants/data-test-ids';
import { useOystehrAPIClient } from 'src/features/visits/shared/hooks/useOystehrAPIClient';
import { useGetCreateExternalLabResources } from 'src/features/visits/shared/stores/appointment/appointment.queries';
import { useDebounce } from 'src/shared/hooks/useDebounce';
import {
  LabListsDTO,
  LabType,
  ModifiedOrderingLocation,
  nameLabTest,
  OrderableItemSearchResult,
  STATIC_COMPENDIUM_LAB_GUID,
} from 'utils';
import { safelyCaptureMessage } from 'utils/lib/frontend/sentry';
import { LabSets } from './LabSets';

type LabsAutocompleteProps = {
  selectedLabs: OrderableItemSearchResult[];
  selectedOrderingLocationId: string;
  labOrgIdsString: string;
  setSelectedLabs: React.Dispatch<React.SetStateAction<OrderableItemSearchResult[]>>;
  labSets: LabListsDTO[] | undefined;
};

export const LabsAutocomplete: FC<LabsAutocompleteProps> = (props) => {
  const { selectedLabs, setSelectedLabs, labOrgIdsString, labSets, selectedOrderingLocationId } = props;
  const [debouncedLabSearchTerm, setDebouncedLabSearchTerm] = useState<string | undefined>(undefined);
  const apiClient = useOystehrAPIClient();

  const {
    isFetching,
    data,
    isError,
    error: resourceFetchError,
  } = useGetCreateExternalLabResources({
    search: debouncedLabSearchTerm,
    labOrgIdsString,
  });

  // coming back from the hook, we expect all these locations to have labGuids in their enabledLabs details
  const orderingLocations = data?.orderingLocations || [];

  const labs = expandResultsForGeneric(data?.labs || [], orderingLocations, selectedOrderingLocationId);

  console.log('labs', JSON.stringify(labs));

  const { debounce } = useDebounce(800);
  const debouncedHandleLabInputChange = (searchValue: string): void => {
    debounce(() => {
      setDebouncedLabSearchTerm(searchValue);
    });
  };

  if (resourceFetchError) console.log('resourceFetchError', resourceFetchError);

  const handleSetSelectedLabsViaLabSets = async (labSet: LabListsDTO): Promise<void> => {
    if (labSet.listType === LabType.external) {
      const res = await apiClient?.getCreateExternalLabResources({
        selectedLabSet: labSet,
      });
      const labs = res?.labs;

      if (labs) {
        setSelectedLabs((currentLabs) => {
          const existingCodes = new Set(currentLabs.map((lab) => `${lab.item.itemCode}${lab.lab.labGuid}`));

          const newLabs = labs.filter((lab) => !existingCodes.has(`${lab.item.itemCode}${lab.lab.labGuid}`));

          return [...currentLabs, ...newLabs];
        });
      }
    }
  };

  return (
    <>
      <Autocomplete
        blurOnSelect
        size="small"
        options={labs}
        getOptionLabel={(option) => nameLabTest(option.item.itemName, option.lab.labName, false)}
        getOptionKey={(lab) => lab.item.uniqueName}
        noOptionsText={
          debouncedLabSearchTerm && labs.length === 0 ? 'No labs based on input' : 'Start typing to load labs'
        }
        value={null}
        onChange={(_, selectedLab: any) => {
          const alreadySelected = selectedLabs.find((tempLab) => {
            return tempLab.item.uniqueName === selectedLab.item.uniqueName;
          });
          if (!alreadySelected) {
            setSelectedLabs([...selectedLabs, selectedLab]);
          } else {
            enqueueSnackbar('This lab is already added to the order', {
              variant: 'error',
            });
          }
        }}
        loading={isFetching}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Lab"
            variant="outlined"
            error={isError}
            helperText={isError ? 'Failed to load labs list' : ''}
            onChange={(e) => debouncedHandleLabInputChange(e.target.value)}
            inputProps={{
              ...params.inputProps,
              'data-testid': dataTestIds.externalLabs.createPg.labsSearchAutoComplete,
            }}
          />
        )}
      />

      {labSets && <LabSets labSets={labSets} setSelectedLabs={handleSetSelectedLabsViaLabSets} />}

      {selectedLabs.length > 0 && (
        <Grid container>
          <Grid item xs={12} data-testid={dataTestIds.externalLabs.createPg.selectedLabContainer}>
            <ActionsList
              data={selectedLabs}
              getKey={(value, index) => `selected-lab-${index}-${value.item.itemCode}`}
              renderItem={(value) => (
                <Typography>{nameLabTest(value.item.itemName, value.lab.labName, false)}</Typography>
              )}
              renderActions={(lab) => (
                <DeleteIconButton
                  onClick={() =>
                    setSelectedLabs((prev) =>
                      prev.filter((tempLab) => {
                        return tempLab.item.uniqueName !== lab.item.uniqueName;
                      })
                    )
                  }
                />
              )}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};

const expandResultsForGeneric = (
  labs: OrderableItemSearchResult[],
  orderingLocations: ModifiedOrderingLocation[],
  selectedLocationId: string
): OrderableItemSearchResult[] => {
  const selectedLocation = orderingLocations.find((location) => location.id === selectedLocationId);
  if (!selectedLocation) {
    console.error('Unable to expand results, returning original labs results');
    safelyCaptureMessage(
      `Unexpected undefined selectedLocation for id ${selectedLocationId} when trying to expandResultsForGeneric`,
      { level: 'warning' }
    );
    return labs;
  }

  // find all the enabled labs that are using the generic compendium
  // sort by the lab name to keep the results sane
  const genericCompendiumLabDetails = selectedLocation.enabledLabs
    .map((lab) => {
      if (lab.labGuid === STATIC_COMPENDIUM_LAB_GUID && lab.labName) return lab;
      return undefined;
    })
    .filter(
      (
        lab
      ): lab is {
        accountNumber: string;
        labOrgRef: string;
        labGuid: string;
        labName: string;
      } => lab !== undefined
    )
    .sort((a, b) => {
      const nameA = a.labName.toLowerCase();
      const nameB = b.labName.toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });

  if (!genericCompendiumLabDetails.length) {
    console.log('No generic labs configured for this location, returning original list', selectedLocationId);
    return labs;
  }

  const expandedResults: OrderableItemSearchResult[] = [];
  // go through each of the orderable item results, and any time you find an item using a generic compendium, expand it
  labs.forEach((orderableItem) => {
    if (orderableItem.lab.labGuid !== STATIC_COMPENDIUM_LAB_GUID) {
      expandedResults.push(orderableItem);
      return;
    }

    genericCompendiumLabDetails.forEach((genericLab) => {
      const editedOrderableItem: OrderableItemSearchResult = {
        ...orderableItem,
        lab: {
          ...orderableItem.lab,
          labName: genericLab.labName,
        },
      };
      expandedResults.push(editedOrderableItem);
    });
  });

  console.log('Expanded result count vs original result', expandedResults.length, labs.length);

  return expandedResults;
};
